import { CompassUserContext } from '@orientaction/Context';
import { useAlert, useRedirectToRootPage, useTestRights } from '@orientaction/hooks';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCompassResultSectionStyles } from './CompassResultSection.styles';
import ResultCard from './ResultCard';

interface MajorTestTemplate {
  id: number;
  shouldBuy?: boolean;
  hasRight?: boolean;
  hasResult?: boolean;
}

interface CompassResultSectionProps<TResult> {
  id?: string;
  title: string;
  description: string;
  templateId: number;
  lastResultId?: number;
  getResult?: (id: number) => Promise<{ data: any }>;
  selectResult?: (data: any) => TResult;
  renderVisual: (result: TResult | undefined, showNeutralVersion: boolean) => ReactNode;
  to: string;
  toResultat: string;
  buyRedirectUri: string;
  externalLoading?: boolean;
}

const isPolicyError = (err: any) =>
  err?.response?.data?.error?.name === 'PolicyError' &&
  err?.response?.data?.error?.status === 403;

const CompassResultSection = <TResult,>({
  id,
  title,
  description,
  templateId,
  lastResultId,
  getResult,
  selectResult,
  renderVisual,
  to,
  toResultat,
  buyRedirectUri,
  externalLoading = false,
}: CompassResultSectionProps<TResult>) => {
  const classes = useCompassResultSectionStyles();
  const [result, setResult] = useState<TResult>();
  const [loading, setLoading] = useState(false);
  const { userInfos, adminView } = useContext<any>(CompassUserContext);
  const { userId }: any = useParams();
  const { canUserAccessTest } = useTestRights(templateId);
  const { goToRootPage } = useRedirectToRootPage();
  const alert = useAlert();
  const getResultRef = useRef(getResult);
  const selectResultRef = useRef(selectResult);
  const alertRef = useRef(alert);
  const goToRootPageRef = useRef(goToRootPage);
  const adminViewRef = useRef(adminView);

  const currentTest = userInfos?.service?.major_tests_templates?.find(
    (test: MajorTestTemplate) => test.id === templateId,
  );
  const shouldBuy = adminView
    ? false
    : currentTest?.shouldBuy || !userInfos?.rattachement || false;
  const hasAccess = adminView
    ? canUserAccessTest
    : currentTest?.hasRight || currentTest?.hasResult || false;
  const hasLoadedResult =
    result !== undefined &&
    result !== null &&
    !(Array.isArray(result) && result.length === 0);
  const showNeutralVersion = adminView
    ? getResult
      ? !hasLoadedResult
      : !hasAccess || lastResultId == null
    : !hasAccess || !currentTest?.hasResult;

  useEffect(() => {
    getResultRef.current = getResult;
    selectResultRef.current = selectResult;
    alertRef.current = alert;
    goToRootPageRef.current = goToRootPage;
    adminViewRef.current = adminView;
  }, [adminView, alert, getResult, goToRootPage, selectResult]);

  useEffect(() => {
    const getResultCurrent = getResultRef.current;
    const selectResultCurrent = selectResultRef.current;

    if (!getResultCurrent || !selectResultCurrent || typeof lastResultId !== 'number') {
      return;
    }

    let active = true;

    const loadResult = async () => {
      setLoading(true);
      try {
        const { data } = await getResultCurrent(lastResultId);
        if (active) {
          setResult(selectResultCurrent(data));
        }
      } catch (err: any) {
        if (!active) {
          return;
        }
        if (isPolicyError(err)) {
          alertRef.current.openSimpleAlert('Cette page ne vous est pas accessible.');
          if (!adminViewRef.current) {
            goToRootPageRef.current();
          }
          return;
        }
        alertRef.current.openSimpleAlert();
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadResult();

    return () => {
      active = false;
    };
  }, [lastResultId]);

  return (
    <section className={classes.sectionBlock} id={id}>
      <h2 className={classes.sectionTitle}>{title}</h2>
      <ResultCard
        visual={renderVisual(result, showNeutralVersion)}
        description={description}
        disable={showNeutralVersion}
        loading={loading || externalLoading}
        to={to}
        toResultat={toResultat}
        withAccess={hasAccess}
        buyRedirectUri={shouldBuy ? buyRedirectUri : undefined}
        adminView={!!adminView}
        beneficiaryId={adminView ? userId : undefined}
      />
    </section>
  );
};

export default CompassResultSection;
